import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { Button } from "@/components/ui/button";
import { Edit3, MoreHorizontal } from "lucide-react";
import "jspdf-autotable";
import { useDownloadTransactionReciept } from "./mutations";
import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

interface IProp {
  id: string;
  onOpenView?: () => void;
  onOpenInvite?: () => void;
  setTransactionId: (v: string) => void;
  setTransaction: () => void;
}
const Options = ({
  id,
  onOpenView,
  onOpenInvite,
  setTransactionId,
  setTransaction,
}: IProp) => {
  const receiptRef = useRef(null);
  const [transactionData, setTransactionData] = useState<any>(null);
  const { mutate: download, } =
    useDownloadTransactionReciept({
      id: id,
      onSuccess: (data) => {
        setTransactionData(data.data.transaction);
      },
    });

  const handlePrint = useReactToPrint({
    documentTitle: `Ticket-${transactionData?.id || "download"}`,
    onAfterPrint: () => toast.success("PDF downloaded successfully"),
    contentRef: receiptRef,
  });

  useEffect(() => {
    if (!transactionData) return;
    handleDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionData]);

  const handleDownload = () => {
    handlePrint();
  };

  return (
    <>
      <div style={{ display: "none" }}>
        <TransactionReceipt
          ref={receiptRef}
          transactionData={transactionData}
        />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="space-y-2 rounded-[10px] p-2"
          align="end"
        >
          <DropdownMenuItem
            onClick={() => {
              onOpenView?.();
              setTransaction();
            }}
            className="flex items-center gap-2 rounded-md bg-[#5B93FF] bg-opacity-5 p-2 text-[#5B93FF]"
          >
            <Edit3 size={16} />
            <span>View Transaction Details</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              download();
            }}
            className="flex items-center gap-2 rounded-md bg-[#5bffa2] bg-opacity-5 p-2 text-[#21814b]"
          >
            <>
              <Edit3 size={16} />
              <span>Download Transaction Reciept</span>
            </>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default Options;

const TransactionReceipt = React.forwardRef<
  HTMLDivElement,
  { transactionData: any }
>(({ transactionData }, ref) => {
  if (!transactionData) return null;

  const {
    amount,
    cardHolder,
    cardType,
    currency,
    date,
    merchantName,
    rrn,
    stan,
    status,
    terminalId,
    transactionId,
    logo,
  } = transactionData;

  return (
    <div ref={ref} className="max-w-xl mx-auto bg-white p-6 font-sans">
      {/* Merchant Name and Logo */}
      <div className="flex justify-center items-center gap-4 mb-4">
        <div className="w-12 h-12 flex items-center justify-center rounded">
          <img
            src={logo}
            alt="Merchant Logo"
            className="max-w-full max-h-full"
          />
        </div>
        <div className="">
          <div className="text-xl font-bold">{merchantName}</div>
        </div>
      </div>

      {/* Receipt Content */}
      <div className="mt-6">
        {/* Date Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Date:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">
            {new Date(date).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>

        {/* Transaction ID Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Transaction ID:</div>
          <div className="w-2/3 py-3 px-4">{transactionId}</div>
        </div>

        {/* Amount Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Amount:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">
            {currency} {amount}
          </div>
        </div>

        {/* Cardholder Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Cardholder:</div>
          <div className="w-2/3 py-3 px-4">{cardHolder}</div>
        </div>

        {/* Card Type Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Card Type:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">{cardType}</div>
        </div>

        {/* RRN Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">RRN:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">{rrn}</div>
        </div>

        {/* STAN Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">STAN:</div>
          <div className="w-2/3 py-3 px-4">{stan}</div>
        </div>

        {/* Status Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Status:</div>
          <div className="w-2/3 py-3 px-4">{status.toUpperCase()}</div>
        </div>

        {/* Terminal ID Row */}
        <div className="flex border-b">
          <div className="w-1/3 py-3 px-4 font-semibold">Terminal ID:</div>
          <div className="w-2/3 py-3 px-4 bg-gray-100">{terminalId}</div>
        </div>
      </div>
    </div>
  );
});
